<template>
    <div class="vertical grow gallery-container" style="height: 100%">
        <div style="margin-top: 20px;">
            <!-- NAME -->
            <div class="info-field horizontal">
                <tr class="info-field-row">
                    <td class="title-td" style="width:200px;"><p class="info-field-title">{{ $t('ShortcutName') }}</p></td>
                    <td style="width: 400px;" class="info-field-value">
                        <input class="datalist-input" style="width: 100%" v-model="shortcut.name"/>
                    </td>
                </tr>
            </div>
            <!-- PRODUCT -->
            <div class="info-field horizontal">
                <tr class="info-field-row">
                    <td class="title-td" style="width:200px;"><p class="info-field-title">{{ $t('Product') }}</p></td>
                    <td style="width: 400px;" class="info-field-value">
                        <input class="datalist-input" list="shortcut-products-list" style="width: 100%" v-model="shortcut.product"/>
                        <datalist id="shortcut-products-list">
                            <option v-for="p in products" :key="p" :value="p">{{ p }}</option>
                        </datalist>
                    </td>
                </tr>
            </div>
            <!-- PLATFORM -->
            <div class="info-field horizontal">
                <tr class="info-field-row">
                    <td class="title-td" style="width:200px;"><p class="info-field-title">{{ $t('Platform') }}</p></td>
                    <td style="width: 400px;" class="info-field-value">
                        <input class="datalist-input" list="shortcut-platforms-list" style="width: 100%" v-model="shortcut.platform"/>
                        <datalist id="shortcut-platforms-list">
                            <option v-for="p in platforms" :key="p" :value="p">{{ p }}</option>
                        </datalist>
                    </td>
                </tr>
            </div>
            <!-- ARCHITECTURE -->
            <div class="info-field horizontal">
                <tr class="info-field-row">
                    <td class="title-td" style="width:200px;"><p class="info-field-title">{{ $t('Architecture') }}</p></td>
                    <td style="width: 400px;" class="info-field-value">
                        <input class="datalist-input" list="shortcut-architectures-list" style="width: 100%" v-model="shortcut.architecture"/>
                        <datalist id="shortcut-architectures-list">
                            <option v-for="p in architectures" :key="p" :value="p">{{ p }}</option>
                        </datalist>
                    </td>
                </tr>
            </div>
            <!-- DESCRIPTION -->
            <div class="info-field horizontal">
                <tr class="info-field-row">
                    <td class="title-td" style="width:200px;"><p class="info-field-title">{{ $t('Description') }}</p></td>
                    <td style="width:400px;" class="info-field-value">
                        <textarea v-model="shortcut.description" type="text" placeholder="Content" style="height: 75px; width: 100%"/>
                    </td>
                </tr>
            </div>
            <!-- ICON -->
            <div class="info-field horizontal">
                <tr class="info-field-row">
                    <td class="title-td" style="width:200px;"><p class="info-field-title">{{ $t('Icon') }}</p></td>
                    <td style="width:400px;" class="info-field-value">
                        <div class="horizontal" style="align-items: center;">
                            <div class="icon-preview" :class="{ empty: !shortcut.iconBase64 }">
                                <img v-if="shortcut.iconBase64" :src="shortcut.iconBase64"/>
                                <span v-else>{{ $t('NoIcon') }}</span>
                            </div>
                            <div class="vertical" style="margin-left: 12px;">
                                <button class="file-select" @click="selectIcon">{{ $t('SelectIcon') }}</button>
                                <button v-if="shortcut.iconBase64" class="link-button" @click="shortcut.iconBase64 = null">{{ $t('RemoveIcon') }}</button>
                                <input ref="iconInput" type="file" accept="image/*" @change="onIconChange" style="display: none;"/>
                            </div>
                        </div>
                    </td>
                </tr>
            </div>
            <div style="justify-content: center; margin-bottom: 10px;" class="horizontal">
                <div class="error" v-if="error != ''">{{ error }}</div>
            </div>
        </div>
        <div style="align-self: center; margin-bottom: 20px;">
            <Button :title="$t('Save')" buttonClass='save-button semi-bold' style="width: 300px; align-self: center;" @onClick="save"></Button>
        </div>
    </div>
</template>

<script>
import { getSoftwareProducts, getSoftwarePlatforms, getArchitectures, addSoftwareShortcut, editSoftwareShortcut } from '../../api/api.js';
import Button from '../Button.vue';

export default {
    components: { Button },
    props: {
        // Existing shortcut to edit, or null when adding a new one.
        current: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            shortcut: {
                id: null,
                name: '',
                product: '',
                platform: '',
                architecture: 'any',
                description: '',
                iconBase64: null
            },
            products: [],
            platforms: [],
            architectures: [],
            error: ''
        }
    },
    methods: {
        async getData() {
            this.products = await getSoftwareProducts();
            this.platforms = await getSoftwarePlatforms();
            this.architectures = await getArchitectures();
        },
        selectIcon() {
            this.$refs.iconInput.click();
        },
        onIconChange(e) {
            var file = e.target.files[0];
            if (!file) {
                return;
            }

            if (file.size > 1048576) {
                this.error = this.$t('IconTooLarge');
                return;
            }

            var reader = new FileReader();
            reader.onload = () => {
                this.shortcut.iconBase64 = reader.result;
            };
            reader.readAsDataURL(file);
        },
        async save() {
            if (!this.shortcut.name || !this.shortcut.product || !this.shortcut.platform) {
                this.error = this.$t('ShortcutRequiredFields');
                return;
            }

            this.error = '';

            try {
                if (this.shortcut.id) {
                    await editSoftwareShortcut(this.shortcut);
                } else {
                    await addSoftwareShortcut(this.shortcut);
                }
                this.$emit('saved');
            } catch (e) {
                console.dir(e);
                this.error = e;
            }
        }
    },
    async mounted() {
        await this.getData();

        if (this.current) {
            this.shortcut = {
                id: this.current.id,
                name: this.current.name,
                product: this.current.product,
                platform: this.current.platform,
                architecture: this.current.architecture || 'any',
                description: this.current.description,
                iconBase64: this.current.iconBase64 || null
            };
        }
    }
}
</script>

<style scoped>
.icon-preview {
    width: 72px;
    height: 72px;
    border-radius: 8px;
    border: 1px solid #E9E9E9;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.icon-preview.empty {
    border-style: dashed;
    color: #9e9e9e;
    font-size: 11px;
    text-align: center;
}

.icon-preview img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.file-select {
    cursor: pointer;
    border-radius: 4px;
    border: 1px solid #34758E;
    background: #34758E;
    color: white;
    padding: 6px 14px;
    font-weight: 600;
    margin-bottom: 6px;
}

.link-button {
    background: transparent;
    border: none;
    color: #346F8B;
    cursor: pointer;
    font-size: 12px;
    text-decoration: underline;
    padding: 0;
}

.error {
    font-size: 14px;
    color: #c0392b;
}
</style>



// WEBPACK FOOTER //
// src/components/Software/SoftwareShortcutModal.vue